import axios from 'axios';

// Strapi API Configuration
const STRAPI_API_URL = import.meta.env?.VITE_STRAPI_API_URL;

console.log('Strapi API URL configured as:', STRAPI_API_URL);

// Create axios instance with base configuration
const strapiAPI = axios?.create({
    baseURL: STRAPI_API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    timeout: 120000, // 2 minutes default (overridden per request when needed)
});

// Request interceptor for adding authentication token
strapiAPI?.interceptors?.request?.use(
    (config) => {
        console.log(`Making ${config?.method?.toUpperCase()} request to:`, config?.url);
        console.log('Request payload:', config?.data);
        
        // Get JWT token from localStorage if available
        const token = localStorage.getItem('strapi_jwt_token');
        
        // CRITICAL FIX: Don't add auth token to login/register requests
        const isAuthRequest = config?.url?.includes('/auth/local') || 
                              config?.url?.includes('/auth/local/register') ||
                              config?.url?.includes('/auth/forgot-password') ||
                              config?.url?.includes('/auth/reset-password');
        
        if (token && !isAuthRequest) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('Added authorization token to request');
        } else if (isAuthRequest) {
            console.log('🔓 Skipping auth token for authentication request');
            // Ensure no authorization header is present for auth requests
            delete config?.headers?.Authorization;
        }
        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for handling errors
strapiAPI?.interceptors?.response?.use(
    (response) => {
        console.log(`Response received from ${response?.config?.url}:`, {
            status: response?.status,
            statusText: response?.statusText,
            hasData: !!response?.data
        });
        return response;
    },
    (error) => {
        // Log minimal error information for debugging (without exposing sensitive details)
        console.log(`Request to ${error?.config?.url} failed with status ${error?.response?.status}`);
        
        // Handle authentication errors
        if (error?.response?.status === 401) {
            console.log('Unauthorized request detected, clearing token');
            // Clear invalid token
            localStorage.removeItem('strapi_jwt_token');
            localStorage.removeItem('user_data');
            // Only redirect if not already on login page
            if (!window.location?.pathname?.includes('/login')) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Strapi API service methods
export const strapiService = {
    // Authentication methods
    auth: {
        login: async (identifier, password) => {
            try {
                console.log('Attempting login with identifier:', identifier);
                
                // Ensure we have clean, valid data
                if (!identifier || !password) {
                    throw new Error('Identifier and password are required');
                }

                const payload = {
                    identifier: identifier?.toString()?.trim(),
                    password: password?.toString()?.trim(),
                };

                console.log('Login payload prepared:', { identifier: payload?.identifier, hasPassword: !!payload?.password });
                
                const response = await strapiAPI?.post('/api/auth/local', payload);
                
                console.log('Login response received:', {
                    status: response?.status,
                    hasJwt: !!response?.data?.jwt,
                    hasUser: !!response?.data?.user,
                    userEmail: response?.data?.user?.email,
                    mustChangePassword: response?.data?.user?.must_change_password
                });
                
                // Store JWT token immediately if present
                if (response?.data?.jwt) {
                    localStorage.setItem('strapi_jwt_token', response?.data?.jwt);
                    console.log('JWT token stored successfully');
                } else {
                    console.warn('No JWT token received in login response');
                }
                
                return response?.data;
            } catch (error) {
                // Log only minimal error information for debugging without exposing sensitive details
                console.log('Authentication failed:', {
                    status: error?.response?.status,
                    url: error?.config?.url
                });
                throw error;
            }
        },

        // NEW: Password change method according to API documentation
        changePassword: async (currentPassword, newPassword) => {
            try {
                console.log('Attempting password change...');
                
                if (!currentPassword || !newPassword) {
                    throw new Error('Current password and new password are required');
                }

                if (newPassword?.length < 6) {
                    throw new Error('New password must be at least 6 characters long');
                }

                const payload = {
                    currentPassword: currentPassword?.toString()?.trim(),
                    newPassword: newPassword?.toString()?.trim(),
                };

                console.log('Password change payload prepared');
                
                const response = await strapiAPI?.post('/api/users/me/change-password', payload);
                
                console.log('Password change response received:', {
                    status: response?.status,
                    message: response?.data?.message
                });
                
                return response?.data;
            } catch (error) {
                console.error('Password change failed:', {
                    status: error?.response?.status,
                    message: error?.response?.data?.message || error?.message
                });
                throw error;
            }
        },
        
        register: async (userData) => {
            try {
                console.log('Attempting registration...');
                const response = await strapiAPI?.post('/api/auth/local/register', userData);
                
                if (response?.data?.jwt) {
                    localStorage.setItem('strapi_jwt_token', response?.data?.jwt);
                    console.log('Registration successful, JWT token stored');
                }
                
                return response?.data;
            } catch (error) {
                console.log('Registration failed with status:', error?.response?.status);
                throw error;
            }
        },
        
        logout: () => {
            console.log('Clearing authentication data...');
            localStorage.removeItem('strapi_jwt_token');
            localStorage.removeItem('user_data');
        },
        
        getProfile: async () => {
            try {
                console.log('Fetching user profile...');
                const response = await strapiAPI?.get('/api/users/me?populate=*');
                console.log('Profile fetched successfully');
                return response?.data;
            } catch (error) {
                console.error('Get profile API call failed:', error);
                throw error;
            }
        },

        // NEW: Get user profile with companies and employees data according to API documentation
        getUserProfile: async () => {
            try {
                console.log('Fetching user profile with companies and employees...');
                const response = await strapiAPI?.get('/api/users/me/profile');

                console.log('User profile with companies fetched successfully:', {
                    hasUser: !!response?.data?.user,
                    companiesCount: response?.data?.companies?.length || 0,
                    userType: response?.data?.user?.user_type
                });

                return response?.data;
            } catch (error) {
                console.error('Get user profile with companies failed:', error);
                throw error;
            }
        },

        updateUserProfile: async (profileData) => {
            try {
                console.log('Updating user profile...');
                const response = await strapiAPI?.put('/api/users/me/update-profile', profileData);

                console.log('User profile updated successfully');

                return response?.data;
            } catch (error) {
                console.error('Update user profile failed:', error);
                throw error;
            }
        },
    },

    // Companies methods - ENHANCED: Add company creation with API documentation requirements
    companies: {
        // NEW: Create company with automatic admin user creation
        createCompany: async (companyData) => {
            try {
                console.log('Creating company with API endpoint /admin/companies:', companyData);
                
                const response = await strapiAPI?.post('/api/admin/companies', companyData);
                
                console.log('Company creation response:', {
                    status: response?.status,
                    hasCompany: !!response?.data?.data?.company,
                    hasAdmin: !!response?.data?.data?.admin,
                    emailSent: response?.data?.emailSent,
                    wasReactivated: response?.data?.wasReactivated,
                    message: response?.data?.message
                });
                
                return response?.data;
            } catch (error) {
                console.error('Create company failed:', {
                    status: error?.response?.status,
                    message: error?.response?.data?.error?.message,
                    name: error?.response?.data?.error?.name
                });
                throw error;
            }
        },

        // NEW: Update company - PUT /api/companies/:id
        updateCompany: async (companyId, companyData) => {
            try {
                console.log(`🔄 Updating company ${companyId} with data:`, companyData);
                
                if (!companyId) {
                    throw new Error('Company ID is required for update');
                }

                // Clean payload - remove undefined values
                const cleanPayload = {};
                Object.keys(companyData)?.forEach(key => {
                    if (companyData?.[key] !== undefined && companyData?.[key] !== null && companyData?.[key] !== '') {
                        cleanPayload[key] = companyData?.[key];
                    }
                });

                console.log('Update company payload prepared:', cleanPayload);
                
                const response = await strapiAPI?.put(`/api/companies/${companyId}`, cleanPayload);
                
                console.log('Company update response:', {
                    status: response?.status,
                    hasData: !!response?.data?.data,
                    message: response?.data?.message,
                    companyName: response?.data?.data?.name
                });
                
                return response?.data;
            } catch (error) {
                console.error('Update company failed:', {
                    status: error?.response?.status,
                    message: error?.response?.data?.error?.message || error?.response?.data?.message,
                    companyId
                });
                throw error;
            }
        },

        getCompanyWithEmployees: async (companyId) => {
            try {
                console.log(`Fetching company with employees for ID: ${companyId}`);
                
                const response = await strapiAPI?.get(`/api/companies/${companyId}`, {
                    params: {
                        populate: 'employees'
                    }
                });
                
                console.log('Company with employees fetched successfully:', {
                    companyId,
                    companyName: response?.data?.data?.name,
                    employeesCount: response?.data?.data?.employees?.length || 0
                });
                
                return response?.data;
            } catch (error) {
                console.error(`Get company ${companyId} with employees failed:`, error);
                throw error;
            }
        },
        
        getCompanyEmployees: async (companyId, filters = {}) => {
            try {
                console.log(`Fetching employees for company ${companyId}:`, filters);
                
                // Build query parameters
                const params = {
                    'filters[company][id][$eq]': companyId,
                    populate: '*'
                };
                
                if (filters?.limit) params.limit = filters?.limit;
                if (filters?.page) params.page = filters?.page;

                const response = await strapiAPI?.get('/api/employees', { 
                    params 
                });
                
                console.log('Company employees fetched successfully:', {
                    companyId,
                    employeesCount: response?.data?.data?.length || 0,
                    filters
                });
                
                return response?.data;
            } catch (error) {
                console.error(`Get company ${companyId} employees failed:`, error);
                throw error;
            }
        }
    },

    // NEW: Users administration methods
    users: {
        // NEW: Reset user password - POST /api/users/reset-password
        // Accepts either userId or employeeId
        resetPassword: async (userIdOrOptions) => {
            try {
                let payload;

                // Handle both old signature (userId) and new signature (options object)
                if (typeof userIdOrOptions === 'object') {
                    payload = userIdOrOptions;
                    console.log('🔄 Resetting password with options:', payload);
                } else {
                    payload = { userId: userIdOrOptions };
                    console.log('🔄 Resetting password for user:', userIdOrOptions);
                }

                if (!payload.userId && !payload.employeeId) {
                    throw new Error('Either userId or employeeId is required');
                }

                console.log('Reset password payload prepared:', payload);

                const response = await strapiAPI?.post('/api/users/reset-password', payload);

                console.log('Reset password response received:', {
                    status: response?.status,
                    message: response?.data?.message,
                    emailSent: response?.data?.data?.emailSent
                });

                return response?.data;
            } catch (error) {
                console.error('Reset password failed:', {
                    status: error?.response?.status,
                    message: error?.response?.data?.message || error?.message
                });
                throw error;
            }
        },

        // NEW: Create company admin - POST /api/users/create-company-admin
        createCompanyAdmin: async (adminData) => {
            try {
                console.log('🔄 Creating company admin...');

                if (!adminData?.email || !adminData?.companyId || !adminData?.firstName || !adminData?.lastName) {
                    throw new Error('All fields are required: email, companyId, firstName, lastName');
                }

                const payload = {
                    email: adminData?.email?.trim(),
                    companyId: adminData?.companyId,
                    firstName: adminData?.firstName?.trim(),
                    lastName: adminData?.lastName?.trim()
                };

                console.log('Create company admin payload prepared:', payload);
                
                const response = await strapiAPI?.post('/api/users/create-company-admin', payload);
                
                console.log('Create company admin response received:', {
                    status: response?.status,
                    message: response?.data?.message,
                    isReactivated: response?.data?.isReactivated,
                    hasUser: !!response?.data?.data
                });
                
                return response?.data;
            } catch (error) {
                console.error('Create company admin failed:', {
                    status: error?.response?.status,
                    message: error?.response?.data?.message || error?.message
                });
                throw error;
            }
        },

        // NEW: Delete company admin - POST /api/users/delete-company-admin
        deleteCompanyAdmin: async (companyId, userId) => {
            try {
                console.log('🗑️ Deleting company admin:', { companyId, userId });

                if (!companyId || !userId) {
                    throw new Error('Company ID and User ID are required');
                }

                const payload = {
                    companyId: companyId,
                    userId: userId
                };

                console.log('Delete company admin payload prepared:', payload);

                const response = await strapiAPI?.post('/api/users/delete-company-admin', payload);

                console.log('Delete company admin response received:', {
                    status: response?.status,
                    message: response?.data?.message,
                    data: response?.data?.data
                });

                return response?.data;
            } catch (error) {
                console.error('Delete company admin failed:', {
                    status: error?.response?.status,
                    message: error?.response?.data?.message || error?.message
                });
                throw error;
            }
        },

        // NEW: Create employee user - POST /api/users/create-employee-user
        createEmployeeUser: async (employeeData) => {
            try {
                console.log('👤 Creating employee user...');

                if (!employeeData?.email || !employeeData?.employeeId) {
                    throw new Error('Email and employeeId are required');
                }

                const payload = {
                    email: employeeData?.email?.trim(),
                    employeeId: employeeData?.employeeId
                };

                console.log('Create employee user payload prepared:', payload);

                const response = await strapiAPI?.post('/api/users/create-employee-user', payload);

                console.log('Create employee user response received:', {
                    status: response?.status,
                    message: response?.data?.message,
                    isReactivated: response?.data?.isReactivated,
                    hasUser: !!response?.data?.data
                });

                return response?.data;
            } catch (error) {
                console.error('Create employee user failed:', {
                    status: error?.response?.status,
                    message: error?.response?.data?.error?.message || error?.message
                });
                throw error;
            }
        }
    },

    // Generic CRUD operations
    get: async (endpoint, params = {}) => {
        try {
            console.log(`GET request to: /api/${endpoint}`, params);
            const response = await strapiAPI?.get(`/api/${endpoint}`, { params });
            return response?.data;
        } catch (error) {
            console.log(`GET /api/${endpoint} failed with status:`, error?.response?.status);
            throw error;
        }
    },

    post: async (endpoint, data) => {
        try {
            console.log(`POST request to: /api/${endpoint}`);
            const response = await strapiAPI?.post(`/api/${endpoint}`, data);
            return response?.data;
        } catch (error) {
            console.log(`POST /api/${endpoint} failed with status:`, error?.response?.status);
            throw error;
        }
    },

    put: async (endpoint, data) => {
        try {
            console.log(`PUT request to: /api/${endpoint}`);
            const response = await strapiAPI?.put(`/api/${endpoint}`, data);
            return response?.data;
        } catch (error) {
            console.log(`PUT /api/${endpoint} failed with status:`, error?.response?.status);
            throw error;
        }
    },

    delete: async (endpoint) => {
        try {
            console.log(`DELETE request to: /api/${endpoint}`);
            const response = await strapiAPI?.delete(`/api/${endpoint}`);
            return response?.data;
        } catch (error) {
            console.log(`DELETE /api/${endpoint} failed with status:`, error?.response?.status);
            throw error;
        }
    },

    // Collection methods with Strapi v5 conventions
    collections: {
        find: async (collectionName, params = {}) => {
            try {
                const response = await strapiAPI?.get(`/api/${collectionName}`, { params });
                return response?.data;
            } catch (error) {
                console.error(`Find collection ${collectionName} failed:`, error);
                throw error;
            }
        },
        
        findOne: async (collectionName, id, params = {}) => {
            try {
                const response = await strapiAPI?.get(`/api/${collectionName}/${id}`, { params });
                return response?.data;
            } catch (error) {
                console.error(`Find one ${collectionName}/${id} failed:`, error);
                throw error;
            }
        },
        
        create: async (collectionName, data) => {
            try {
                const response = await strapiAPI?.post(`/api/${collectionName}`, { data });
                return response?.data;
            } catch (error) {
                console.error(`Create ${collectionName} failed:`, error);
                throw error;
            }
        },
        
        update: async (collectionName, id, data) => {
            try {
                const response = await strapiAPI?.put(`/api/${collectionName}/${id}`, { data });
                return response?.data;
            } catch (error) {
                console.error(`Update ${collectionName}/${id} failed:`, error);
                throw error;
            }
        },
        
        delete: async (collectionName, id) => {
            try {
                const response = await strapiAPI?.delete(`/api/${collectionName}/${id}`);
                return response?.data;
            } catch (error) {
                console.error(`Delete ${collectionName}/${id} failed:`, error);
                throw error;
            }
        },
    },

    // File upload methods - ENHANCED: Add logo upload functionality
    upload: {
        // NEW: Upload logo specifically for company - FIXED: Enhanced error handling
        uploadLogo: async (file) => {
            try {
                console.log('🔄 Starting logo upload process...');
                console.log('File details:', {
                    name: file?.name,
                    size: file?.size,
                    type: file?.type
                });

                // Validate file before upload
                if (!file) {
                    throw new Error('No file provided for upload');
                }

                // Check if API URL is configured
                if (!STRAPI_API_URL) {
                    console.error('❌ STRAPI_API_URL is not configured');
                    throw new Error('API URL no está configurado. Contacte al administrador.');
                }

                console.log('📡 Uploading to:', `${STRAPI_API_URL}/api/upload`);
                
                const formData = new FormData();
                formData?.append('files', file);

                // Create a custom axios request with enhanced error handling
                const uploadResponse = await strapiAPI?.post('/api/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    timeout: 30000, // 30 second timeout for file uploads
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent?.loaded * 100) / progressEvent?.total);
                        console.log(`📤 Upload progress: ${percentCompleted}%`);
                    }
                });

                // Validate response
                if (!uploadResponse?.data || !Array.isArray(uploadResponse?.data) || uploadResponse?.data?.length === 0) {
                    console.error('❌ Invalid upload response:', uploadResponse?.data);
                    throw new Error('Respuesta inválida del servidor');
                }

                const uploadedFile = uploadResponse?.data?.[0];
                
                if (!uploadedFile?.id) {
                    console.error('❌ No file ID in upload response:', uploadedFile);
                    throw new Error('Error al procesar el archivo subido');
                }

                console.log('✅ Logo upload successful:', {
                    fileId: uploadedFile?.id,
                    fileName: uploadedFile?.name,
                    fileUrl: uploadedFile?.url,
                    fileSize: uploadedFile?.size
                });

                return uploadedFile;

            } catch (error) {
                console.error('❌ Logo upload failed:', {
                    message: error?.message,
                    status: error?.response?.status,
                    statusText: error?.response?.statusText,
                    data: error?.response?.data,
                    code: error?.code,
                    config: {
                        url: error?.config?.url,
                        method: error?.config?.method,
                        timeout: error?.config?.timeout
                    }
                });

                // Enhanced error messages for different scenarios
                if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
                    throw new Error('Error de conexión: No se pudo conectar con el servidor. Verifique su conexión a internet.');
                }
                
                if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
                    throw new Error('Tiempo de espera agotado: El archivo es muy grande o la conexión es lenta.');
                }
                
                if (error?.response?.status === 413) {
                    throw new Error('El archivo es demasiado grande. Máximo permitido: 2MB.');
                }
                
                if (error?.response?.status === 401) {
                    throw new Error('No autorizado: Inicie sesión nuevamente.');
                }
                
                if (error?.response?.status === 403) {
                    throw new Error('Sin permisos para subir archivos.');
                }
                
                if (error?.response?.status === 415) {
                    throw new Error('Tipo de archivo no soportado. Use JPG, PNG o GIF.');
                }
                
                if (error?.response?.status >= 500) {
                    throw new Error('Error del servidor: Intente nuevamente en unos minutos.');
                }

                // Extract server error message if available
                const serverMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
                if (serverMessage) {
                    throw new Error(`Error del servidor: ${serverMessage}`);
                }
                
                // Fallback error message
                throw new Error(error?.message || 'Error desconocido al subir el logo');
            }
        },

        single: async (file, refId = null, ref = null, field = null) => {
            try {
                console.log('Uploading single file...');
                const formData = new FormData();
                formData?.append('files', file);
                
                if (refId) formData?.append('refId', refId);
                if (ref) formData?.append('ref', ref);
                if (field) formData?.append('field', field);

                const response = await strapiAPI?.post('/api/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                return response?.data;
            } catch (error) {
                console.error('Single file upload failed:', error);
                throw error;
            }
        },
        
        multiple: async (files, refId = null, ref = null, field = null) => {
            try {
                console.log('Uploading multiple files...');
                const formData = new FormData();
                files?.forEach(file => {
                    formData?.append('files', file);
                });
                
                if (refId) formData?.append('refId', refId);
                if (ref) formData?.append('ref', ref);
                if (field) formData?.append('field', field);

                const response = await strapiAPI?.post('/api/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                return response?.data;
            } catch (error) {
                console.error('Multiple file upload failed:', error);
                throw error;
            }
        },
    },

    /**
     * Sincronizaciones SAT que corren en el SERVIDOR (jul-2026).
     *
     * El navegador ya no conduce el proceso: crea el trabajo y observa. Puede
     * cerrarse la pantalla sin consecuencias — el orquestador sigue avanzando la
     * solicitud y al volver se ve dónde quedó.
     */
    satSync: {
        /**
         * Encola una sincronización. El backend valida la cobertura antes de
         * gastar cupo del SAT: puede responder 409 pidiendo una decisión
         * (`aceptarSugerencia` para pedir solo el hueco, `forzar` para insistir).
         */
        crear: async ({ companyId, dateFrom, dateTo, aceptarSugerencia = false, forzar = false }) => {
            try {
                const response = await strapiAPI?.post('/api/sat-sync/requests', {
                    company_id: companyId,
                    date_from: dateFrom,
                    date_to: dateTo,
                    aceptar_sugerencia: aceptarSugerencia,
                    forzar,
                });
                return response?.data;
            } catch (e) {
                // Un 409 aquí NO es un fallo: es el servidor contestando "este
                // periodo ya lo tienes" o "ya hay una en curso". Axios rechaza
                // cualquier 4xx, así que se devuelve el cuerpo para que la
                // pantalla pregunte al usuario en vez de mostrar un error rojo.
                if (e?.response?.status === 409 && e?.response?.data) return e.response.data;
                throw e;
            }
        },

        /** Empresas que hoy pueden sincronizar (activas + e.firma completa). */
        empresasElegibles: async () => {
            const response = await strapiAPI?.get('/api/sat-sync/batch/eligible');
            return response?.data;
        },

        /**
         * Crea un lote multi-empresa. Con `simular: true` NO crea nada: solo
         * calcula qué pasaría con cada empresa, que es lo que se enseña antes de
         * confirmar algo irreversible.
         */
        crearLote: async ({ companyIds, dateFrom, dateTo, simular = false, todas = false }) => {
            const response = await strapiAPI?.post('/api/sat-sync/batch', {
                company_ids: companyIds, date_from: dateFrom, date_to: dateTo, simular, todas,
            });
            return response?.data;
        },

        /**
         * Dispara el barrido diario a mano (el mismo de las 00:00).
         * Con `simular` calcula a quién se le pediría sin gastar cupo.
         */
        barrido: async ({ dia = null, simular = false } = {}) => {
            const response = await strapiAPI?.post('/api/sat-sync/sweep', { dia, simular });
            return response?.data;
        },

        /** Los últimos lotes con su avance. */
        lotes: async (limite = 20) => {
            const response = await strapiAPI?.get('/api/sat-sync/batches', { params: { limit: limite } });
            return response?.data;
        },

        /** Cómo va un lote concreto, con sus solicitudes. */
        lote: async (batchId) => {
            const response = await strapiAPI?.get(`/api/sat-sync/batch/${encodeURIComponent(batchId)}`);
            return response?.data;
        },

        /** Mapa por día: qué se pidió y qué trajo (eje = fecha de emisión). */
        cobertura: async ({ companyId, from, to }) => {
            const response = await strapiAPI?.get('/api/sat-sync/coverage', {
                params: { company_id: companyId, from, to },
            });
            return response?.data;
        },

        /** Qué pasaría si se pidiera este periodo, ANTES de gastar cupo. */
        revisarPeriodo: async ({ companyId, from, to }) => {
            const response = await strapiAPI?.get('/api/sat-sync/coverage/check', {
                params: { company_id: companyId, from, to },
            });
            return response?.data;
        },

        /** Histórico auditable: nada se borra. */
        historico: async (filtros = {}) => {
            const params = {};
            if (filtros.companyId) params.company_id = filtros.companyId;
            if (filtros.status) params.status = filtros.status;
            if (filtros.origin) params.origin = filtros.origin;
            if (filtros.from) params.from = filtros.from;
            if (filtros.to) params.to = filtros.to;
            if (filtros.page) params.page = filtros.page;
            if (filtros.limit) params.limit = filtros.limit;
            const response = await strapiAPI?.get('/api/sat-sync/requests', { params });
            return response?.data;
        },

        /** Lo que está corriendo ahora mismo (monitor en vivo). */
        enCurso: async (companyId) => {
            const params = companyId ? { company_id: companyId } : {};
            const response = await strapiAPI?.get('/api/sat-sync/live', { params });
            return response?.data;
        },

        detalle: async (id) => {
            const response = await strapiAPI?.get(`/api/sat-sync/requests/${id}`);
            return response?.data;
        },

        cancelar: async (id) => {
            const response = await strapiAPI?.post(`/api/sat-sync/requests/${id}/cancel`);
            return response?.data;
        },

        reintentar: async (id) => {
            const response = await strapiAPI?.post(`/api/sat-sync/requests/${id}/retry`);
            return response?.data;
        },
    },

    // Employee Payroll Receipts methods
    payroll: {
        // Recibos de TODA la empresa agrupados por periodo de nómina (pantalla "Recibos").
        getCompanyReceiptPeriods: async (companyId, filters = {}) => {
            const params = {};
            if (filters?.year) params.year = filters.year;
            const response = await strapiAPI?.get(`/api/companies/${companyId}/receipt-periods`, { params });
            return response?.data;
        },

        // Recibos de la empresa en lista: de un periodo concreto o los que coincidan
        // con la búsqueda por nombre, RFC, folio o UUID.
        getCompanyReceipts: async (companyId, filters = {}) => {
            const params = {};
            if (filters?.periodStart) params.period_start = filters.periodStart;
            if (filters?.periodEnd) params.period_end = filters.periodEnd;
            if (filters?.search) params.search = filters.search;
            if (filters?.year) params.year = filters.year;
            if (filters?.page) params.page = filters.page;
            if (filters?.limit) params.limit = filters.limit;
            const response = await strapiAPI?.get(`/api/companies/${companyId}/receipts`, { params });
            return response?.data;
        },

        // Get receipts for a specific employee (Admin flow)
        getEmployeeReceipts: async (employeeId, filters = {}) => {
            try {
                console.log(`Fetching payroll receipts for employee ${employeeId}:`, filters);

                // Build query parameters
                const params = {};
                if (filters?.year) params.year = filters?.year;
                if (filters?.month) params.month = filters?.month;
                if (filters?.limit) params.limit = filters?.limit;

                const response = await strapiAPI?.get(`/api/employees/${employeeId}/payroll-receipts`, {
                    params
                });

                console.log('Employee payroll receipts fetched successfully:', {
                    employeeId,
                    receiptsCount: response?.data?.data?.length || 0,
                    filters
                });

                return response?.data;
            } catch (error) {
                console.error(`Get employee ${employeeId} payroll receipts failed:`, error);
                throw error;
            }
        },

        // Get receipts for logged-in employee (Employee flow)
        getMyReceipts: async (filters = {}) => {
            try {
                console.log('Fetching my payroll receipts:', filters);

                // Build query parameters
                const params = {};
                if (filters?.year) params.year = filters?.year;
                if (filters?.month) params.month = filters?.month;
                if (filters?.limit) params.limit = filters?.limit;

                const response = await strapiAPI?.get('/api/employees/me/payroll-receipts', {
                    params
                });

                console.log('My payroll receipts fetched successfully:', {
                    receiptsCount: response?.data?.data?.length || 0,
                    filters
                });

                return response?.data;
            } catch (error) {
                console.error('Get my payroll receipts failed:', error);
                throw error;
            }
        },

        // Download PDF for a payroll receipt
        downloadPDF: async (receiptId) => {
            try {
                console.log(`📄 Downloading PDF for receipt ${receiptId}...`);

                const response = await strapiAPI?.get(`/api/payroll-receipts/${receiptId}/pdf`, {
                    responseType: 'blob' // Important: tell axios to expect binary data
                });

                console.log('✅ PDF downloaded successfully:', {
                    receiptId,
                    size: response?.data?.size,
                    type: response?.data?.type
                });

                return response?.data; // Returns Blob
            } catch (error) {
                console.error(`❌ Download PDF for receipt ${receiptId} failed:`, error);
                throw error;
            }
        },

        // Download XML for a payroll receipt
        downloadXML: async (receiptId) => {
            try {
                console.log(`📄 Downloading XML for receipt ${receiptId}...`);

                const response = await strapiAPI?.get(`/api/payroll-receipts/${receiptId}/download-xml`, {
                    responseType: 'blob' // Important: tell axios to expect binary data
                });

                console.log('✅ XML downloaded successfully:', {
                    receiptId,
                    size: response?.data?.size,
                    type: response?.data?.type
                });

                return response?.data; // Returns Blob
            } catch (error) {
                console.error(`❌ Download XML for receipt ${receiptId} failed:`, error);
                throw error;
            }
        }
    },

    // Dashboard analytics methods
    dashboard: {
        getStats: async () => {
            try {
                const response = await strapiAPI?.get('/api/dashboard/stats');
                return response?.data;
            } catch (error) {
                console.error('Get dashboard stats failed:', error);
                throw error;
            }
        },

        getCompanyPerformance: async () => {
            try {
                const response = await strapiAPI?.get('/api/dashboard/company-performance');
                return response?.data;
            } catch (error) {
                console.error('Get company performance failed:', error);
                throw error;
            }
        },

        getMonthlyTrends: async () => {
            try {
                const response = await strapiAPI?.get('/api/dashboard/monthly-trends');
                return response?.data;
            } catch (error) {
                console.error('Get monthly trends failed:', error);
                throw error;
            }
        },

        getFIELStatus: async () => {
            try {
                const response = await strapiAPI?.get('/api/dashboard/fiel-status');
                return response?.data;
            } catch (error) {
                console.error('Get FIEL status failed:', error);
                throw error;
            }
        },

        getSATSyncHistory: async () => {
            try {
                const response = await strapiAPI?.get('/api/dashboard/sat-sync-history');
                return response?.data;
            } catch (error) {
                console.error('Get SAT sync history failed:', error);
                throw error;
            }
        }
    },

    // Prospectos (BO super_admin) — captados por el asistente de WhatsApp
    prospects: {
        list: async (params = {}) => {
            const response = await strapiAPI?.get('/api/admin/prospects', { params });
            return response?.data; // { data: [...], meta: { pagination } }
        },
        stats: async () => {
            const response = await strapiAPI?.get('/api/admin/prospects/stats');
            return response?.data?.data;
        },
        get: async (id) => {
            const response = await strapiAPI?.get(`/api/admin/prospects/${id}`);
            return response?.data?.data;
        },
        update: async (id, patch) => {
            const response = await strapiAPI?.patch(`/api/admin/prospects/${id}`, patch);
            return response?.data?.data;
        },
    },
};

// Export the configured axios instance for custom requests
export default strapiAPI;

// Export configuration
export const config = {
    apiUrl: STRAPI_API_URL,
    getAuthToken: () => localStorage.getItem('strapi_jwt_token'),
    isAuthenticated: () => !!localStorage.getItem('strapi_jwt_token'),
};